/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.I18N.MANAGER — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/I18nManagerPage.tsx. The desktop
 * screen is a full translations grid (screen prompts + data-content _tl rows,
 * inline-editable, with find/replace and AI translate). That dense editing surface
 * is a desktop workflow; the mobile-native view is a purpose-built, read-only
 * roster of the active-locale set — the same csm_language_master the desktop reads
 * (§16: never the desktop grid re-hosted in a phone viewport).
 *
 * Each row is one configured locale: its code, its human name resolved via
 * localeName() (§17.B4 — never a bare code alone), and a Y/N active status pill.
 * Reads only via apiDb; no writes here, so nothing to route through the Common
 * Routine — locale add/remove and string editing stay on the desktop admin.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Star } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { localeName, localeFlag } from '../../i18n/locale_display';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

interface MobileLocaleRow {
  language_id: number;
  internal_code: string;
  is_active: 'Y' | 'N';
  is_default: 'Y' | 'N';
}

type StatusFacet = 'all' | 'active' | 'inactive';

export function MobileI18nManagerPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [locales, setLocales] = useState<MobileLocaleRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileLocaleRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: lErr } = await apiDb
        .from('csm_language_master')
        .select('language_id, internal_code, is_active, is_default')
        .order('internal_code');
      if (lErr) throw lErr;
      const rows = ((data ?? []) as MobileLocaleRow[]).map((r) => ({
        language_id: r.language_id,
        internal_code: r.internal_code,
        is_active: r.is_active,
        is_default: r.is_default,
      }));
      setLocales(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (names follow it).
  useEffect(() => { void reload(); }, [activeLocale]);

  // Human name for a locale, resolved into the active UI language (§17.B4).
  const nameOf = (code: string) => localeName(code, activeLocale);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return locales.filter((l) => {
      if (statusFacet === 'active' && l.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && l.is_active !== 'N') return false;
      if (!q) return true;
      return l.internal_code.toLowerCase().includes(q) || nameOf(l.internal_code).toLowerCase().includes(q);
    });
    // nameOf depends on activeLocale, already a dep via the effect-driven list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locales, filter, statusFacet, activeLocale]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.i18n.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const isCurrent = (l: MobileLocaleRow) => l.internal_code === activeLocale;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.i18n.title', 'Languages')}
        description={t('mobile.i18n.description', 'The configured locales for this workspace and their status.')}
      />

      <MobileStatStrip
        stats={[
          { value: locales.length, label: t('mobile.i18n.stat.total', 'Total') },
          { value: locales.filter((l) => l.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
          { value: locales.filter((l) => l.is_active === 'N').length, label: t('common.boolean.inactive', 'Inactive') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.i18n.search', 'Search languages')}
          aria-label={t('mobile.i18n.search', 'Search languages')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.i18n.none', 'No languages match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((l) => (
            <MobileCardRow
              key={l.language_id}
              lead={localeFlag(l.internal_code)}
              leadTone={isCurrent(l) ? 'blue' : 'slate'}
              title={nameOf(l.internal_code)}
              subtitle={
                l.internal_code
                + (l.is_default === 'Y' ? ' · ' + t('mobile.i18n.default', 'Default') : '')
                + (isCurrent(l) ? ' · ' + t('mobile.i18n.current', 'Current') : '')
              }
              trailing={
                <MobileStatusPill tone={l.is_active === 'Y' ? 'ok' : 'no'}>
                  {l.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(l)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.i18n.detail', 'Language detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av" aria-hidden>{localeFlag(selected.internal_code)}</span>
              <span>
                <div className="m-nm">{nameOf(selected.internal_code)}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.i18n.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.i18n.default', 'Default')}
                value={
                  <MobileStatusPill tone={selected.is_default === 'Y' ? 'ok' : 'no'}>
                    {selected.is_default === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.i18n.code', 'Code')} value={selected.internal_code} />
              {isCurrent(selected) && (
                <MobileDetailRow
                  label={t('mobile.i18n.current', 'Current')}
                  value={<><Star size={14} /> {t('mobile.i18n.current_hint', 'Active UI language')}</>}
                />
              )}
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
